import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { isProEntitlement } from '../utils/entitlements';
import { sendAssignmentNotification, sendAssignmentUpdateNotification } from '../services/emailNotifications';
import { runSupabaseAuthedOperation, toUserMessage } from '../utils/asyncReliability';
import { devLog, devError } from '../utils/logger';

const Scheduling = ({ quotes: quotesProp = [] }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('schedulingActiveTab');
    if (savedTab === 'projects') return 'calendar';
    return savedTab || 'calendar';
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('appointment');
  const [formData, setFormData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [selectedDayData, setSelectedDayData] = useState({});
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [dayAssignments, setDayAssignments] = useState([]);
  const [allDayAssignments, setAllDayAssignments] = useState({});
  const [assignmentType, setAssignmentType] = useState('project');
  const [selectedProjectForAssignment, setSelectedProjectForAssignment] = useState('');
  const [manualAssignmentNotes, setManualAssignmentNotes] = useState('');
  const [selectedTeamMemberForAssignment, setSelectedTeamMemberForAssignment] = useState('');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [assignmentStartDate, setAssignmentStartDate] = useState('');
  const [assignmentEndDate, setAssignmentEndDate] = useState('');
  const [selectedWorkDays, setSelectedWorkDays] = useState([]);
  const [showTeamMemberSchedule, setShowTeamMemberSchedule] = useState(false);
  const [selectedTeamMemberForSchedule, setSelectedTeamMemberForSchedule] = useState(null);
  const [teamMemberScheduleDate, setTeamMemberScheduleDate] = useState(new Date());
  const [showWeekends] = useState(false);
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  
  // Client search state for searchable dropdown
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  
  // Project search state for searchable dropdown
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [isEditingAssignment, setIsEditingAssignment] = useState(false);
  const [editingAssignmentGroup, setEditingAssignmentGroup] = useState(null);
  const [showTeamMemberDayDetail, setShowTeamMemberDayDetail] = useState(false);
  const [selectedTeamMemberDay, setSelectedTeamMemberDay] = useState(null);
  const [showOldAppointments, setShowOldAppointments] = useState(false);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  
  // Data states
  const [projects, setProjects] = useState([]);
  const [teams, setTeams] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [selectedClientQuotes, setSelectedClientQuotes] = useState([]);
  const [showQuoteSelection, setShowQuoteSelection] = useState(false);
  
  const navigate = useNavigate();

  const runAuthedProfileWrite = async (name, updates, targetUserId = user?.id) => {
    if (!targetUserId) throw new Error('No user');
    const result = await runSupabaseAuthedOperation({
      name,
      supabaseClient: supabase,
      timeoutMs: 50000,
      retries: 1,
      fn: async () => {
        const { error } = await supabase.from('profiles').update(updates).eq('id', targetUserId);
        if (error) throw error;
      },
    });
    if (!result.ok) {
      throw result.error || new Error(result.userMessage || 'Could not save changes.');
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      setLoading(false);

      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        return;
      }

      if (!currentUser) {
        if (event === 'SIGNED_OUT') {
          navigate('/', { replace: true });
        } else {
          navigate('/account', { replace: true });
        }
        return;
      }

      // Check if user has Pro plan
      try {
        const { data: userData, error } = await supabase.from('profiles').select('*').eq('id', currentUser.id).single();
        if (error) throw error;
        if (userData) {
          // Redirect if not Pro plan
          if (!isProEntitlement(userData)) {
            navigate('/dashboard');
            return;
          }
          
          // Load user data
          await loadUserData(currentUser.id);
        } else {
          navigate('/dashboard');
        }
      } catch (error) {
        devError('Error checking user plan:', error);
        navigate('/dashboard');
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- session listener mounts once; `loadUserData` is stable enough for this screen
  }, [navigate]);

  // Update quotes when prop changes
  useEffect(() => {
    setQuotes(quotesProp || []);
  }, [quotesProp]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('schedulingActiveTab', activeTab);
  }, [activeTab]);

  const loadUserData = async (userId) => {
    try {
      const { data: userData, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
      if (error) throw error;
      if (userData) {
        setProjects(userData.projects || []);
        setTeams(userData.teams || []);
        setAppointments(userData.appointments || []);
        setClients(userData.clients || []);
        setQuotes(quotesProp || []);
        setAllDayAssignments(userData.day_assignments || userData.dayAssignments || {});

        // Clean up any orphaned assignments after loading data
        await cleanupOrphanedAssignments();
      }
    } catch (error) {
      devError('Error loading user data:', error);
    }
  };

  const handleAddItem = async () => {
    if (!formData.name || !formData.name.trim()) {
      alert('Please enter a name');
      return;
    }

    setIsSubmitting(true);
    try {
      let updatedItems;
      
      if (editingItem) {
        // Editing existing item
        const currentItems = getCollectionName(modalType) === 'teams' ? teams : appointments;
        
        // Clean formData to remove undefined values for editing
        const cleanFormData = Object.fromEntries(
          Object.entries(formData).filter(([key, value]) => value !== undefined && value !== null)
        );
        
        updatedItems = currentItems.map(item => 
          item.id === editingItem.id 
            ? { 
                ...item, 
                ...cleanFormData, 
                name: formData.name.trim(),
                // Ensure both field names are saved for backward compatibility
                associatedQuotes: formData.associatedQuotes || formData.selectedQuoteId,
                selectedQuoteId: formData.selectedQuoteId || formData.associatedQuotes
              }
            : item
        );
      } else {
        // Adding new item - clean formData to remove undefined values
        const cleanFormData = Object.fromEntries(
          Object.entries(formData).filter(([key, value]) => value !== undefined && value !== null)
        );
        
        const newItem = {
          id: Date.now().toString(),
          name: formData.name.trim(),
          ...cleanFormData,
          // Ensure both field names are saved for backward compatibility
          associatedQuotes: formData.associatedQuotes || formData.selectedQuoteId,
          selectedQuoteId: formData.selectedQuoteId || formData.associatedQuotes,
          createdAt: new Date().toISOString()
        };

        updatedItems = [newItem, ...(getCollectionName(modalType) === 'teams' ? teams : appointments)];
      }

      // AGGRESSIVE DATA CLEANING - Remove ALL undefined values recursively
      const deepClean = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== 'object') return obj;
        
        if (Array.isArray(obj)) {
          return obj.map(item => deepClean(item)).filter(item => item !== null);
        }
        
        const cleaned = {};
        for (const [key, value] of Object.entries(obj)) {
          if (value !== undefined && value !== null) {
            const cleanedValue = deepClean(value);
            if (cleanedValue !== null) {
              cleaned[key] = cleanedValue;
            }
          }
        }
        return cleaned;
      };

      const cleanedItems = deepClean(updatedItems);
      devLog('CLEANED DATA:', JSON.stringify(cleanedItems, null, 2));
      
      await runAuthedProfileWrite('scheduling:saveModalItems', {
        [getCollectionName(modalType)]: cleanedItems
      });

      if (getCollectionName(modalType) === 'teams') {
        setTeams(updatedItems);
      } else {
        setAppointments(updatedItems);
      }

      setFormData({});
      setEditingItem(null);
      setShowAddModal(false);
      setModalType('appointment');
      setClientSearch('');
      setShowClientDropdown(false);
    } catch (error) {
      devError('Error saving item:', error);
      alert(toUserMessage(error, 'Failed to save item. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCollectionName = (type) => {
    switch (type) {
      case 'team': return 'teams';
      case 'appointment': return 'appointments';
      default: return 'appointments';
    }
  };

  // Filter appointments by date
  const getCurrentAppointments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appointments.filter(appointment => {
      if (!appointment.date) return true; // Keep appointments without dates
      const appointmentDate = new Date(appointment.date);
      return appointmentDate >= today;
    });
  };

  const getOldAppointments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return appointments.filter(appointment => {
      if (!appointment.date) return false; // Skip appointments without dates
      const appointmentDate = new Date(appointment.date);
      return appointmentDate < today;
    });
  };

  const handleDeleteItem = async (itemId, itemType) => {
    // Set the item to delete and show the confirmation modal
    setItemToDelete({ id: itemId, type: itemType });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      const collectionName = getCollectionName(itemToDelete.type);
      const currentItems = collectionName === 'teams' ? teams : appointments;

      const updatedItems = currentItems.filter(item => item.id !== itemToDelete.id);

      await runAuthedProfileWrite('scheduling:deleteItem', {
        [collectionName]: updatedItems
      });

      if (collectionName === 'teams') {
        setTeams(updatedItems);
        
        // Clean up ALL assignments for this team member (both current and future)
        const updatedAllDayAssignments = { ...allDayAssignments };
        let hasChanges = false;
        
        Object.keys(updatedAllDayAssignments).forEach(dateKey => {
          const originalLength = updatedAllDayAssignments[dateKey].length;
          updatedAllDayAssignments[dateKey] = updatedAllDayAssignments[dateKey].filter(
            assignment => assignment.memberId !== itemToDelete.id
          );
          if (updatedAllDayAssignments[dateKey].length !== originalLength) {
            hasChanges = true;
          }
          
          // Remove empty date keys
          if (updatedAllDayAssignments[dateKey].length === 0) {
            delete updatedAllDayAssignments[dateKey];
          }
        });
        
        if (hasChanges) {
          setAllDayAssignments(updatedAllDayAssignments);
          
          await runAuthedProfileWrite('scheduling:deleteItemCleanupAssignments', {
            day_assignments: updatedAllDayAssignments
          });
          
          devLog(`Cleaned up all assignments for deleted team member: ${itemToDelete.id}`);
        }
        
        // Clean up any remaining orphaned assignments
        await cleanupOrphanedAssignments();
        
      } else {
        setAppointments(updatedItems);
      }

      // Close the modal and reset the item to delete
      setShowDeleteModal(false);
      setItemToDelete(null);
    } catch (error) {
      devError('Error deleting item:', error);
      alert(toUserMessage(error, 'Failed to delete item. Please try again.'));
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleEditItem = (item, type) => {
    setFormData(item);
    setEditingItem(item);
    setModalType(type);
    setShowAddModal(true);
    
    // Set client search if editing an item with a client
    if (item.client) {
      setClientSearch(item.client);
    } else {
      setClientSearch('');
    }
    
  };

  const handleClientSelection = (clientName) => {
    const selectedClient = clients.find(client => client.name === clientName);
    if (selectedClient) {
      const clientQuotes = quotes.filter(quote => quote.clientId === selectedClient.id);
      setSelectedClientQuotes(clientQuotes);
    }
  };

  const handleQuoteFieldClick = () => {
    if (formData.client) {
      const selectedClient = clients.find(client => client.name === formData.client);
      if (selectedClient) {
        const clientQuotes = quotes.filter(quote => quote.clientId === selectedClient.id);
        setSelectedClientQuotes(clientQuotes);
        setShowQuoteSelection(true);
      }
    }
  };

  const handleQuoteSelection = (quoteId) => {
    setFormData({ ...formData, associatedQuotes: quoteId, selectedQuoteId: quoteId }); // Keep both for backward compatibility
    setShowQuoteSelection(false);
  };

  const handleAssignTeamMemberFromTeams = (memberId, type) => {
    setSelectedTeamMemberForAssignment(memberId);
    setAssignmentType(type);
    setIsEditingAssignment(false);
    setEditingAssignmentGroup(null);
    setShowAssignmentModal(true);
  };

  const handleViewTeamMemberSchedule = (member) => {
    setSelectedTeamMemberForSchedule(member);
    setShowTeamMemberSchedule(true);
  };

  const cleanupOrphanedAssignments = async () => {
    try {
      if (!user || !user.id) {
        devLog('User not available for cleanup');
        return;
      }
      
      const updatedAssignments = {};
      
      Object.keys(allDayAssignments).forEach(dateKey => {
        const assignments = allDayAssignments[dateKey];
        const validAssignments = assignments.filter(assignment => {
          // Check if the team member still exists
          const teamMemberExists = teams.some(team => team.id === assignment.memberId);
          if (!teamMemberExists) {
            return false;
          }
          
          // Check if project still exists (for project assignments)
          if (assignment.assignmentType === 'project') {
            return projects.some(project => project.id === assignment.projectId);
          }
          
          return true;
        });
        
        if (validAssignments.length > 0) {
          updatedAssignments[dateKey] = validAssignments;
        }
      });
      
      await runAuthedProfileWrite('scheduling:cleanupOrphanedAssignments', {
        day_assignments: updatedAssignments
      });
        
      setAllDayAssignments(updatedAssignments);
    } catch (error) {
      devError('Error cleaning up assignments:', error);
    }
  };

  const handleTeamMemberScheduleDayClick = (date) => {
    const dateKey = date.toISOString().split('T')[0];
    const dayAssignments = allDayAssignments[dateKey] || [];
    const memberAssignments = dayAssignments.filter(assignment => 
      assignment.memberId === selectedTeamMemberForSchedule.id
    );
    
    setSelectedTeamMemberDay({
      date: date,
      assignments: memberAssignments
    });
    setShowTeamMemberDayDetail(true);
  };

  const refreshDayDetailModal = () => {
    if (selectedTeamMemberDay) {
      const dateKey = selectedTeamMemberDay.date.toISOString().split('T')[0];
      const dayAssignments = allDayAssignments[dateKey] || [];
      const memberAssignments = dayAssignments.filter(assignment => 
        assignment.memberId === selectedTeamMemberForSchedule.id
      );
      
      setSelectedTeamMemberDay({
        ...selectedTeamMemberDay,
        assignments: memberAssignments
      });
    }
  };

  const handleRemoveSingleDayAssignment = async (assignmentId, date) => {
    try {
      if (!user || !user.id) {
        alert('User not available. Please refresh the page.');
        return;
      }
      
      const dateKey = date.toISOString().split('T')[0];
      const updatedAllAssignments = { ...allDayAssignments };
      
      if (updatedAllAssignments[dateKey]) {
        updatedAllAssignments[dateKey] = updatedAllAssignments[dateKey].filter(
          assignment => assignment.id !== assignmentId
        );
        
        // Remove the date key if no assignments remain
        if (updatedAllAssignments[dateKey].length === 0) {
          delete updatedAllAssignments[dateKey];
        }
      }

      await runAuthedProfileWrite('scheduling:removeSingleDayAssignment', {
        day_assignments: updatedAllAssignments
      });

      setAllDayAssignments(updatedAllAssignments);
    } catch (error) {
      devError('Error removing single day assignment:', error);
      alert(toUserMessage(error, 'Failed to remove assignment. Please try again.'));
    }
  };

  const handleSaveAssignmentFromTeams = async () => {
    if (!selectedTeamMemberForAssignment || !assignmentStartDate || !assignmentEndDate) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const startDate = new Date(assignmentStartDate);
      const endDate = new Date(assignmentEndDate);
    const assignments = [];

      // Generate assignments for each selected work day
      selectedWorkDays.forEach(workDay => {
        const workDate = new Date(workDay);
        if (workDate >= startDate && workDate <= endDate) {
      const assignment = {
            id: isEditingAssignment ? 
              editingAssignmentGroup.assignments.find(a => {
                const aDate = a.date?.toDate ? a.date.toDate() : new Date(a.date);
                return aDate.toDateString() === workDate.toDateString();
              })?.id || Date.now().toString() + Math.random().toString(36).substr(2, 9) :
              Date.now().toString() + Math.random().toString(36).substr(2, 9),
        memberId: selectedTeamMemberForAssignment,
            memberName: teams.find(t => t.id === selectedTeamMemberForAssignment)?.name || 'Unknown',
            date: workDate,
        assignmentType: assignmentType,
            projectId: selectedProjectForAssignment,
            projectName: projects.find(p => p.id === selectedProjectForAssignment)?.name || '',
            notes: manualAssignmentNotes,
            startDate: startDate,
            endDate: endDate,
            workDays: selectedWorkDays,
            createdAt: isEditingAssignment ? 
              editingAssignmentGroup.assignments[0].createdAt : 
              new Date().toISOString()
          };
          assignments.push(assignment);
        }
      });

      // Update assignments in all relevant dates
      const updatedAllAssignments = { ...allDayAssignments };
      
      if (isEditingAssignment) {
        // Remove old assignments first
        const oldAssignmentIds = editingAssignmentGroup.assignments.map(a => a.id);
        Object.keys(updatedAllAssignments).forEach(dateKey => {
          if (updatedAllAssignments[dateKey]) {
            updatedAllAssignments[dateKey] = updatedAllAssignments[dateKey].filter(
              assignment => !oldAssignmentIds.includes(assignment.id)
            );
          }
        });
      }

      // Add new/updated assignments
      assignments.forEach(assignment => {
        const dateKey = assignment.date.toISOString().split('T')[0];
        const currentAssignments = updatedAllAssignments[dateKey] || [];
        updatedAllAssignments[dateKey] = [...currentAssignments, assignment];
        });
        
      await runAuthedProfileWrite('scheduling:saveAssignmentFromTeams', {
        day_assignments: updatedAllAssignments
      });

      setAllDayAssignments(updatedAllAssignments);
      
      // Send email notifications to team members
      if (sendEmailNotification) {
        try {
          const teamMember = teams.find(t => t.id === selectedTeamMemberForAssignment);
          if (teamMember && teamMember.email) {
            if (assignmentType === 'project' && selectedProjectForAssignment) {
              const project = projects.find(p => p.id === selectedProjectForAssignment);
              if (project) {
                const assignmentData = {
                  startDate: startDate,
                  endDate: endDate,
                  workDays: selectedWorkDays,
                  notes: manualAssignmentNotes
                };
                
                if (isEditingAssignment) {
                  // Send update notification
                  await sendAssignmentUpdateNotification(assignmentData, teamMember, project);
                } else {
                  // Send new assignment notification
                  await sendAssignmentNotification(assignmentData, teamMember, project);
                }
              }
            }
          }
        } catch (emailError) {
          devError('Error sending email notification:', emailError);
          // Don't show error to user as assignment was saved successfully
        }
      }
      
      // Refresh day detail modal if it's open
      if (showTeamMemberDayDetail && selectedTeamMemberDay) {
        refreshDayDetailModal();
      }
      
      // Reset form and editing state
      setSelectedTeamMemberForAssignment('');
      setAssignmentType('project');
      setSelectedProjectForAssignment('');
      setManualAssignmentNotes('');
      setAssignmentStartDate('');
      setAssignmentEndDate('');
      setSelectedWorkDays([]);
      setIsEditingAssignment(false);
      setEditingAssignmentGroup(null);
      setSendEmailNotification(true); // Reset to default
      setShowAssignmentModal(false);
      setProjectSearch('');
      setShowProjectDropdown(false);
    } catch (error) {
      devError('Error saving assignment:', error);
      alert(toUserMessage(error, 'Failed to save assignment. Please try again.'));
    }
  };


  const handleRemoveAssignmentGroup = async (assignments) => {
    if (!window.confirm('Are you sure you want to remove all assignments in this group?')) return;

    try {
      const assignmentIds = assignments.map(assignment => assignment.id);
      const updatedAllAssignments = {};
      
      Object.keys(allDayAssignments).forEach(dateKey => {
        const dateAssignments = allDayAssignments[dateKey];
        const filteredAssignments = dateAssignments.filter(assignment => 
          !assignmentIds.includes(assignment.id)
        );
        
        if (filteredAssignments.length > 0) {
          updatedAllAssignments[dateKey] = filteredAssignments;
        }
      });
      
      await runAuthedProfileWrite('scheduling:removeAssignmentGroup', {
        day_assignments: updatedAllAssignments
      });
      
      setAllDayAssignments(updatedAllAssignments);
    } catch (error) {
      devError('Error removing assignment group:', error);
      alert(toUserMessage(error, 'Failed to remove assignment group. Please try again.'));
    }
  };

  const handleEditAssignment = (group) => {
    // Set up the assignment modal with the group's data
    setSelectedTeamMemberForAssignment(group.assignments[0].memberId);
    setAssignmentType(group.assignmentType);
    setSelectedProjectForAssignment(group.assignmentType === 'project' ? group.assignments[0].projectId : '');
    setManualAssignmentNotes(group.assignmentType === 'manual' ? group.notes : '');
    setAssignmentStartDate(group.startDate?.toDate ? group.startDate.toDate().toISOString().split('T')[0] : new Date(group.startDate).toISOString().split('T')[0]);
    setAssignmentEndDate(group.endDate?.toDate ? group.endDate.toDate().toISOString().split('T')[0] : new Date(group.endDate).toISOString().split('T')[0]);
    setSelectedWorkDays(group.workDays || []);
    
    // Set editing state
    setIsEditingAssignment(true);
    setEditingAssignmentGroup(group);
    
    // Set email notification to true by default for editing
    setSendEmailNotification(true);
    
    // Close the schedule modal and open the assignment modal
    setShowTeamMemberSchedule(false);
    setShowAssignmentModal(true);
  };

  const handleSendAssignmentNotification = async (group) => {
    const teamMember = teams.find(t => t.id === group.assignments[0].memberId);
    if (!teamMember || !teamMember.email) {
      alert('No email address found for this team member.');
      return;
    }

    // Ask for confirmation
    const confirmSend = window.confirm(
      `Send update notification to ${teamMember.name} (${teamMember.email})?\n\nThis will send an email with the current assignment details.`
    );
    
    if (!confirmSend) return;

    try {

      if (group.assignmentType === 'project' && group.assignments[0].projectId) {
        const project = projects.find(p => p.id === group.assignments[0].projectId);
        if (project) {
          const assignmentData = {
            startDate: group.startDate,
            endDate: group.endDate,
            workDays: group.workDays || [],
            notes: group.notes
          };
          
          await sendAssignmentUpdateNotification(assignmentData, teamMember, project, false);
          alert('Update notification sent successfully!');
        }
      } else if (group.assignmentType === 'manual') {
        // For manual assignments, we'll send a custom notification
        const assignmentData = {
          startDate: group.startDate,
          endDate: group.endDate,
          workDays: group.workDays || [],
          notes: group.notes
        };
        
        // Create a mock project object for manual assignments
        const mockProject = {
          name: 'Manual Assignment',
          client: 'Internal'
        };
        
        await sendAssignmentUpdateNotification(assignmentData, teamMember, mockProject, false);
        alert('Update notification sent successfully!');
      }
    } catch (error) {
      devError('Error sending assignment notification:', error);
      alert('Failed to send notification. Please try again.');
    }
  };

  const handleDayClick = (date) => {
    // Get appointments and projects for this day
    const dayAppointments = appointments.filter(appointment => {
      const appointmentDate = appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date);
      return appointmentDate.toDateString() === date.toDateString();
    });

    const dayProjects = projects.filter(project => {
      const startDate = project.startDate?.toDate ? project.startDate.toDate() : new Date(project.startDate);
      const deadline = project.deadline?.toDate ? project.deadline.toDate() : (project.deadline ? new Date(project.deadline) : null);
      
      // Check if the date falls within the project timeline
      if (deadline) {
        return date >= startDate && date <= deadline;
      } else {
        return date.toDateString() === startDate.toDateString();
      }
    });

    setSelectedDayData({
      date,
      appointments: dayAppointments,
      projects: dayProjects
    });
    setShowDayModal(true);
    
    const loadDayAssignments = async () => {
        try {
          const dateKey = date.toISOString().split('T')[0];
        const assignments = allDayAssignments[dateKey] || [];
        setDayAssignments(assignments);
        } catch (error) {
          devError('Error loading day assignments:', error);
      }
    };

    loadDayAssignments();
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const calendar = [];
    const currentDateObj = new Date(startDate);
    
    while (currentDateObj <= lastDay || calendar.length < 42) {
      calendar.push(new Date(currentDateObj));
      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
                
                return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Calendar Header */}
        <div className="bg-primary text-white p-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{monthNames[month]} {year}</h2>
              <p className="text-cyan-100">Manage your schedule and team assignments</p>
                  </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => setCurrentDate(new Date())}
                className="px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors text-sm font-medium"
              >
                Today
              </button>
              <button
                onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="p-4">
          {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center py-3 text-sm font-semibold text-gray-600 bg-gray-50 rounded-lg">
                {day}
              </div>
            ))}
        </div>

          {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
            {calendar.map((date, index) => {
              const isCurrentMonth = date.getMonth() === month;
              const isToday = date.toDateString() === new Date().toDateString();
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const dateKey = date.toISOString().split('T')[0];
              const dayAssignments = allDayAssignments[dateKey] || [];
              
              // Get appointments for this day
              const dayAppointments = appointments.filter(appointment => {
                const appointmentDate = appointment.date?.toDate ? appointment.date.toDate() : new Date(appointment.date);
                return appointmentDate.toDateString() === date.toDateString();
              });
              
              // Get projects that span this day
              const dayProjects = projects.filter(project => {
                const startDate = project.startDate?.toDate ? project.startDate.toDate() : new Date(project.startDate);
                const deadline = project.deadline?.toDate ? project.deadline.toDate() : (project.deadline ? new Date(project.deadline) : null);
                
                // Check if the date falls within the project timeline
                if (deadline) {
                  return date >= startDate && date <= deadline;
                } else {
                  return date.toDateString() === startDate.toDateString();
                }
              });
              
              // Only show team assignments if this day is clicked/selected
              const isSelectedDay = selectedDayData?.date?.toDateString() === date.toDateString();
              const projectAssignments = isSelectedDay ? dayAssignments.filter(a => a.assignmentType === 'project') : [];
              const manualAssignments = isSelectedDay ? dayAssignments.filter(a => a.assignmentType === 'manual') : [];

              return (
                <div
                  key={index}
                  onClick={() => handleDayClick(date)}
                  className={`min-h-[100px] p-2 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                    isCurrentMonth 
                      ? 'bg-white border-gray-200 hover:border-cyan-300' 
                      : 'bg-gray-50 border-gray-100 text-gray-400'
                  } ${
                    isToday ? 'border-cyan-500 bg-cyan-50' : ''
                  } ${
                    isWeekend ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className={`text-sm font-medium mb-2 ${
                    isToday ? 'text-cyan-600' : 
                    isCurrentMonth ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {date.getDate()}
            </div>
            
                  {isCurrentMonth && (
                    <div className="space-y-1">
                      {/* Appointments */}
                      {dayAppointments.slice(0, 2).map((appointment, idx) => (
                        <div
                          key={appointment.id}
                          className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-lg truncate"
                          title={`${appointment.name} - ${appointment.time || 'No time set'}`}
                        >
                          {appointment.name}
            </div>
                      ))}
                      
                      {/* Projects */}
                      {dayProjects.slice(0, 2).map((project, idx) => (
                        <div
                          key={project.id}
                          className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-lg truncate"
                          title={`${project.name} - ${project.client || 'No client'}`}
                        >
                          {project.name}
            </div>
                      ))}
                      
                      {/* Team Assignments (only show if day is selected) */}
                      {isSelectedDay && (
                        <>
                          {projectAssignments.slice(0, 2).map((assignment, idx) => (
                            <div
                              key={assignment.id}
                              className="text-xs px-2 py-1 bg-cyan-100 text-cyan-800 rounded-full truncate"
                              title={`${assignment.memberName} - ${assignment.projectName}`}
                            >
                              👤 {assignment.memberName.split(' ')[0]} • {assignment.projectName}
                            </div>
                          ))}
                          
                          {manualAssignments.slice(0, 2).map((assignment, idx) => (
                            <div
                              key={assignment.id}
                              className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded-full truncate"
                              title={`${assignment.memberName} - ${assignment.notes}`}
                            >
                              👤 {assignment.memberName.split(' ')[0]} • Manual
          </div>
                          ))}
                        </>
                      )}
                      
                      {/* More indicators */}
                      {(() => {
                        const totalItems = dayAppointments.length + dayProjects.length + (isSelectedDay ? projectAssignments.length + manualAssignments.length : 0);
                        const shownItems = Math.min(dayAppointments.length, 2) + Math.min(dayProjects.length, 2) + (isSelectedDay ? Math.min(projectAssignments.length, 2) + Math.min(manualAssignments.length, 2) : 0);
                        
                        if (totalItems > shownItems) {
                          return (
                            <div className="text-xs text-gray-500 text-center">
                              +{totalItems - shownItems} more
                    </div>
                          );
                        }
                        return null;
                      })()}
                  </div>
                  )}
            </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderTeams = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
        <div>
            <h2 className="text-2xl font-bold text-gray-900">Team Members</h2>
            <p className="text-gray-600">Manage your construction team</p>
          </div>
          <button
            onClick={() => {
              setModalType('team');
              setFormData({});
              setEditingItem(null);
              setShowAddModal(true);
            }}
            className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors shadow-sm"
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Team Member</span>
            </div>
          </button>
        </div>
      </div>

      {/* Teams Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map(team => (
          <div key={team.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200 flex flex-col h-full">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{team.name}</h3>
                <div className="space-y-2">
                  {team.role && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                      <span className="text-sm">{team.role}</span>
                </div>
                  )}
                  {team.phone && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span className="text-sm">{team.phone}</span>
                </div>
                  )}
                  {team.email && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">{team.email}</span>
              </div>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleViewTeamMemberSchedule(team)}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                  title="View Schedule"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleEditItem(team, 'team')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteItem(team.id, 'team')}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="flex-grow">
              {team.notes && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">{team.notes}</p>
                </div>
              )}
            </div>
            
            {/* Assignment Buttons - Moved to bottom */}
            <div className="mt-4 space-y-2">
              <div className="flex space-x-2">
                <button
                  onClick={() => handleAssignTeamMemberFromTeams(team.id, 'project')}
                  className="flex-1 px-3 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Project
                </button>
                <button
                  onClick={() => handleAssignTeamMemberFromTeams(team.id, 'manual')}
                  className="flex-1 px-3 py-2 bg-accent text-white text-sm rounded-lg hover:bg-accent-dark transition-colors"
                >
                  Manual
                </button>
              </div>
              <button
                onClick={() => handleViewTeamMemberSchedule(team)}
                className="w-full px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                View Schedule
              </button>
            </div>
          </div>
        ))}
              </div>
              
        {teams.length === 0 && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No team members yet</h3>
          <p className="text-gray-600 mb-6">Add your first team member to start scheduling</p>
              <button
            onClick={() => {
              setModalType('team');
              setFormData({});
              setEditingItem(null);
              setShowAddModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-200"
          >
            Add First Team Member
              </button>
          </div>
        )}
    </div>
  );

  const renderAppointments = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Appointments</h2>
            <p className="text-gray-600">Manage client meetings and site visits</p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setShowOldAppointments(!showOldAppointments)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
            >
              {showOldAppointments ? 'Hide Old Appointments' : 'View Old Appointments'}
            </button>
            <button
              onClick={() => {
                setModalType('appointment');
                setFormData({});
                setEditingItem(null);
                setShowAddModal(true);
              }}
              className="px-6 py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary-dark transition-colors shadow-sm"
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Add Appointment</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Current Appointments List */}
      {!showOldAppointments && (
        <div className="space-y-4">
          {getCurrentAppointments().map(appointment => (
          <div key={appointment.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{appointment.name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {appointment.time && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{appointment.time}</span>
              </div>
                  )}
                  {appointment.client && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="text-sm">{appointment.client}</span>
                    </div>
                  )}
                  {appointment.location && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="text-sm">{appointment.location}</span>
                    </div>
                  )}
                  {appointment.date && (
                    <div className="flex items-center text-gray-600">
                      <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm">{new Date(appointment.date).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
                
                {appointment.description && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">{appointment.description}</p>
                  </div>
                )}
              </div>
              
              <div className="flex space-x-2 ml-4">
                <button
                  onClick={() => handleEditItem(appointment, 'appointment')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Edit"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDeleteItem(appointment.id, 'appointment')}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Old Appointments Section */}
      {showOldAppointments && getOldAppointments().length > 0 && (
        <div className="mt-8">
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Past Appointments</h3>
            <p className="text-sm text-gray-600">Appointments that have already passed</p>
          </div>
          <div className="space-y-4">
            {getOldAppointments().map(appointment => (
              <div key={appointment.id} className="bg-gray-50 rounded-lg border border-gray-200 p-6 opacity-75">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-700 mb-3">{appointment.name}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {appointment.time && (
                        <div className="flex items-center text-gray-500">
                          <svg className="w-4 h-4 mr-2 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm">{appointment.time}</span>
                        </div>
                      )}
                      {appointment.client && (
                        <div className="flex items-center text-gray-500">
                          <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm">{appointment.client}</span>
                        </div>
                      )}
                      {appointment.location && (
                        <div className="flex items-center text-gray-500">
                          <svg className="w-4 h-4 mr-2 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span className="text-sm">{appointment.location}</span>
                        </div>
                      )}
                      {appointment.date && (
                        <div className="flex items-center text-gray-500">
                          <svg className="w-4 h-4 mr-2 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-sm">{new Date(appointment.date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    {appointment.description && (
                      <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">{appointment.description}</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEditItem(appointment, 'appointment')}
                      className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteItem(appointment.id, 'appointment')}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state for old appointments */}
      {showOldAppointments && getOldAppointments().length === 0 && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No past appointments</h3>
          <p className="text-gray-600 mb-6">You don't have any past appointments to view</p>
        </div>
      )}

        {!showOldAppointments && getCurrentAppointments().length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No current appointments</h3>
            <p className="text-gray-600 mb-6">
              {getOldAppointments().length > 0 
                ? "You have past appointments. Click 'View Old Appointments' to see them."
                : "Schedule your first client meeting or site visit"
              }
            </p>
            <button
              onClick={() => {
                setModalType('appointment');
                setFormData({});
                setEditingItem(null);
                setShowAddModal(true);
              }}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all duration-200"
          >
            Schedule First Appointment
          </button>
          </div>
        )}
    </div>
  );

  const renderModal = () => {
    if (!showAddModal) return null;

    const getModalFields = () => {
      switch (modalType) {
        case 'team':
          return [
            { name: 'name', label: 'Name', type: 'text', required: true },
            { name: 'role', label: 'Role', type: 'text', required: true },
            { name: 'phone', label: 'Phone', type: 'tel', required: false },
            { name: 'email', label: 'Email', type: 'email', required: false },
            { name: 'hourlyRate', label: 'Hourly Rate ($)', type: 'number', required: false, step: '0.01', min: '0' },

          ];

        case 'appointment':
          return [
            { name: 'name', label: 'Appointment Name', type: 'text', required: true },
            { name: 'date', label: 'Date', type: 'date', required: true },
            { name: 'time', label: 'Time', type: 'time', required: true },
            { name: 'client', label: 'Client', type: 'searchable-client', required: false },
            { name: 'location', label: 'Location', type: 'text', required: false },
            { name: 'description', label: 'Description', type: 'textarea', required: false }
          ];
        case 'reminder':
          return [
            { name: 'name', label: 'Reminder Title', type: 'text', required: true },
            { name: 'type', label: 'Type', type: 'select', options: ['Follow-up Call', 'Payment Due', 'Deadline', 'Maintenance', 'Material Order', 'Site Visit'], required: true },
            { name: 'date', label: 'Due Date', type: 'date', required: true },
            { name: 'time', label: 'Time', type: 'time', required: false },
            { name: 'priority', label: 'Priority', type: 'select', options: ['Low', 'Medium', 'High', 'Urgent'], required: true },
            { name: 'client', label: 'Related Client', type: 'searchable-client', required: true },
            { name: 'description', label: 'Description', type: 'textarea', required: false }
          ];
        default:
          return [];
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {editingItem ? 'Edit' : 'Add'} {modalType.charAt(0).toUpperCase() + modalType.slice(1)}
            </h3>
            <button
              onClick={() => { 
                setShowAddModal(false); 
                setFormData({}); 
                setEditingItem(null); 
                setClientSearch('');
                setShowClientDropdown(false);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={(e) => { 
            e.preventDefault(); 
            setIsSubmitting(true);
            handleAddItem(); 
          }}>
            <div className="space-y-4">
              {getModalFields().map(field => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.label}
                  </label>
                  {field.type === 'select' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required={field.required}
                    >
                      <option value="">Select {field.label}</option>
                      {field.options.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  ) : field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      rows="3"
                      required={field.required}
                    />
                  ) : field.type === 'select-client' ? (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => {
                        setFormData({ ...formData, [field.name]: e.target.value });
                        if (e.target.value) {
                          handleClientSelection(e.target.value);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required={field.required}
                    >
                      <option value="">Select {field.label}</option>
                      {clients.map(client => (
                        <option key={client.id || client.name} value={client.name}>{client.name}</option>
                      ))}
                    </select>
                  ) : field.type === 'searchable-client' ? (
                    <div className="relative">
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => {
                          setClientSearch(e.target.value);
                          setShowClientDropdown(true);
                        }}
                        onFocus={() => setShowClientDropdown(true)}
                        onBlur={() => {
                          // Delay hiding dropdown to allow clicking
                          setTimeout(() => setShowClientDropdown(false), 200);
                        }}
                        placeholder="Search for a client..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        required={field.required}
                      />
                      {showClientDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {clients
                            .filter(client => 
                              client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                              client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                            )
                            .map(client => (
                              <div
                                key={client.id}
                                onClick={() => {
                                  setFormData({ ...formData, [field.name]: client.name });
                                  setClientSearch(client.name);
                                  setShowClientDropdown(false);
                                  handleClientSelection(client.name);
                                }}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                              >
                                <div className="font-medium text-gray-900">{client.name}</div>
                                {client.email && (
                                  <div className="text-xs text-gray-500">{client.email}</div>
                                )}
                              </div>
                            ))}
                          {clients.filter(client => 
                            client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                            client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-gray-500 text-sm">
                              No clients found
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : field.type === 'select-quote' ? (
                    <div>
                      <div
                        onClick={handleQuoteFieldClick}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent cursor-pointer bg-white"
                      >
                        {formData[field.name] || formData.selectedQuoteId ? (
                          (() => {
                            const quoteId = formData[field.name] || formData.selectedQuoteId;
                            const selectedQuote = selectedClientQuotes.find(q => q.id === quoteId);
                            return selectedQuote ? `${selectedQuote.quoteNumber || `Quote #${selectedQuote.id?.slice(-6)}`}` : 'Select Quote (Required)';
                          })()
                        ) : (
                          'Select Quote (Required)'
                        )}
                      </div>
                      {selectedClientQuotes.length === 0 && formData.client && (
                        <p className="text-sm text-red-500 mt-1">No invoices found for this client. Create an invoice first.</p>
                      )}
                    </div>
                  ) : field.type === 'checkbox' ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData[field.name] || false}
                        onChange={(e) => setFormData({ ...formData, [field.name]: e.target.checked })}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-sm text-gray-600">Include Saturday and Sunday in project timeline</span>
                    </div>
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      required={field.required}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                type="button"
                onClick={() => { 
                  setShowAddModal(false); 
                  setFormData({}); 
                  setEditingItem(null); 
                  setClientSearch('');
                  setShowClientDropdown(false);
                }}
                className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (editingItem ? 'Saving...' : 'Adding...') : (editingItem ? 'Save Changes' : `Add ${modalType.charAt(0).toUpperCase() + modalType.slice(1)}`)}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const renderQuoteSelectionModal = () => {
    if (!showQuoteSelection) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Select Quote for Project
            </h3>
            <button
              onClick={() => setShowQuoteSelection(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {selectedClientQuotes.length > 0 ? (
              selectedClientQuotes.map(quote => (
                <div
                  key={quote.id}
                  onClick={() => handleQuoteSelection(quote.id)}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-900">{quote.quoteNumber || `Quote #${quote.id?.slice(-6)}`}</h4>
                      {quote.createdAt && !isNaN(new Date(quote.createdAt).getTime()) && (
                        <p className="text-sm text-gray-600">Created: {new Date(quote.createdAt).toLocaleDateString()}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Quote
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No invoices found for this client</p>
                <p className="text-sm text-gray-400 mt-2">You must create a quote for this client before creating a project.</p>
                <button
                  onClick={() => setShowQuoteSelection(false)}
                  className="mt-4 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDayModal = () => {
    if (!showDayModal) return null;

    const { date, appointments, projects } = selectedDayData;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-3xl font-bold text-primary mb-2">
                {date.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </h3>
              <p className="text-gray-600">Manage your schedule for this day</p>
            </div>
            <button
              onClick={() => setShowDayModal(false)}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>



          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Appointments */}
              <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 min-h-[400px] max-h-[500px] overflow-y-auto">
                <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Appointments ({appointments.length})
                </h4>
                <div className="space-y-3">
                  {appointments.map(appointment => (
                    <div key={appointment.id} className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-4 hover:shadow-soft transition-all duration-200">
                      <div className="flex items-center justify-between mb-3">
                        <h5 className="font-semibold text-primary">{appointment.title}</h5>
                          <button
                            onClick={() => {
                              handleDeleteItem(appointment.id, 'appointment');
                              setShowDayModal(false);
                            }}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        {appointment.time && (
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{appointment.time}</span>
                          </div>
                        )}
                        {appointment.client && (
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span>{appointment.client}</span>
                          </div>
                        )}
                        {appointment.location && (
                          <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{appointment.location}</span>
                          </div>
                        )}
                        {appointment.description && (
                          <div className="mt-2 text-gray-600 bg-white/50 rounded p-2">
                            {appointment.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {appointments.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>No appointments scheduled</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Projects */}
              <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 min-h-[400px] max-h-[500px] overflow-y-auto">
                <h4 className="text-lg font-semibold text-accent mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Projects ({projects.length})
                </h4>
                <div className="space-y-3">
                  {projects.map(project => (
                    <div key={project.id} className="bg-gradient-to-r from-accent/5 to-accent/10 border border-accent/20 rounded-lg p-4 hover:shadow-soft transition-all duration-200">
                      <div className="mb-3">
                        <h5 className="font-semibold text-accent-dark">{project.name}</h5>
                      </div>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span>{project.client}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                          <span>${project.budget || 0}</span>
                        </div>
                        {project.description && (
                          <div className="mt-2 text-gray-600 bg-white/50 rounded p-2">
                            {project.description}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {projects.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <p>No projects scheduled</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">


              {/* Team Members */}
              <div className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 min-h-[400px] max-h-[500px] overflow-y-auto">
                <h4 className="text-lg font-semibold text-primary mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Team Members ({dayAssignments.length})
                </h4>
                


                {/* Assigned Team Members */}
                <div className="space-y-2">
                  {dayAssignments.map(assignment => (
                    <div key={assignment.id} className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-3 hover:shadow-soft transition-all duration-200">
                      <div className="mb-2">
                        <h5 className="font-semibold text-primary text-sm">{assignment.memberName}</h5>
                      </div>
                      <div className="text-xs text-gray-700 space-y-0.5">
                        {assignment.memberRole && (
                          <div className="flex items-center space-x-1">
                            <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H8a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                            </svg>
                            <span>{assignment.memberRole}</span>
                          </div>
                        )}
                        {assignment.assignedAt && (
                          <div className="flex items-center space-x-1">
                            <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{(assignment.assignedAt?.toDate ? assignment.assignedAt.toDate() : new Date(assignment.assignedAt)).toLocaleDateString()}</span>
                          </div>
                        )}
                        {/* Assignment Type and Details */}
                        <div className="flex items-center space-x-1">
                          <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">
                            {assignment.assignmentType === 'project' ? 'Project' : 'Manual'}
                          </span>
                        </div>
                        {assignment.assignmentType === 'project' && assignment.projectName && (
                          <div className="flex items-center space-x-1">
                            <svg className="w-3 h-3 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <span className="text-accent-dark font-medium">{assignment.projectName}</span>
                          </div>
                        )}
                        {assignment.assignmentType === 'manual' && assignment.notes && (
                          <div className="mt-1 p-1.5 bg-white/50 rounded text-gray-600">
                            <span className="font-medium">Notes:</span> {assignment.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {dayAssignments.length === 0 && (
                    <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <p>No team members assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header with Back Button */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Scheduling</h1>
          <p className="text-gray-600">Calendar, teams, and appointments — manage projects in the Projects section</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span>Back to Dashboard</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="flex">
            {[
              { 
                id: 'calendar', 
                label: 'Calendar', 
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )
              },
              { 
                id: 'teams', 
                label: 'Teams', 
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                )
              },

              { 
                id: 'appointments', 
                label: 'Appointments', 
                icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                )
              },

            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-4 px-6 font-medium transition-all duration-200 border-b-2 ${
                  activeTab === tab.id
                    ? 'text-cyan-600 border-cyan-600 bg-cyan-50'
                    : 'text-gray-600 border-transparent hover:text-cyan-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  {tab.icon}
                  <span>{tab.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {activeTab === 'calendar' && (
            <>
              {renderCalendar()}
              
              {/* Worker Assignments Overview - Only on Calendar Tab */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Worker Assignments
                </h3>
                
                {Object.keys(allDayAssignments).length === 0 ? (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-sm">No worker assignments found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(() => {
                      // Collect all assignments and group by team member
                      const allAssignments = [];
                      Object.entries(allDayAssignments).forEach(([dateKey, assignments]) => {
                        assignments.forEach(assignment => {
                          allAssignments.push({
                            ...assignment,
                            dateKey,
                            date: new Date(dateKey)
                          });
                        });
                      });

                      // Group by team member and find next upcoming work day
                      const groupedByMember = {};
                      const today = new Date();
                      today.setHours(0, 0, 0, 0); // Start of today

                      allAssignments.forEach(assignment => {
                        if (!groupedByMember[assignment.memberId]) {
                          groupedByMember[assignment.memberId] = {
                            memberId: assignment.memberId,
                            memberName: assignment.memberName,
                            memberRole: assignment.memberRole,
                            nextWorkDay: null,
                            nextWorkDayAssignment: null
                          };
                        }
                        
                        // Check if this assignment is in the future
                        const assignmentDate = new Date(assignment.dateKey);
                        assignmentDate.setHours(0, 0, 0, 0);
                        
                        if (assignmentDate >= today) {
                          // If no next work day set yet, or this date is earlier than current next work day
                          if (!groupedByMember[assignment.memberId].nextWorkDay || 
                              assignmentDate < groupedByMember[assignment.memberId].nextWorkDay) {
                            groupedByMember[assignment.memberId].nextWorkDay = assignmentDate;
                            groupedByMember[assignment.memberId].nextWorkDayAssignment = assignment;
                          }
                        }
                      });

                      // Sort by next work day (earliest first)
                      const sortedMembers = Object.values(groupedByMember)
                        .filter(member => member.nextWorkDay) // Only show members with upcoming work
                        .sort((a, b) => a.nextWorkDay - b.nextWorkDay);

                                              return sortedMembers.map(member => {
                          const nextDate = member.nextWorkDay;
                          const assignment = member.nextWorkDayAssignment;
                          const isToday = nextDate.toDateString() === today.toDateString();
                          const isTomorrow = nextDate.toDateString() === new Date(today.getTime() + 24 * 60 * 60 * 1000).toDateString();
                          
                          return (
                            <div key={member.memberId} className="border border-gray-200 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center space-x-2">
                                  <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white font-semibold text-xs">
                                    {member.memberName?.charAt(0) || 'U'}
                                  </div>
                                  <div>
                                    <h4 className="font-medium text-gray-900 text-sm">{member.memberName}</h4>
                                    <p className="text-xs text-gray-600">{member.memberRole}</p>
                                  </div>
                                </div>
                                <span className={`text-xs px-2 py-1 rounded font-medium ${
                                  isToday 
                                    ? 'bg-green-100 text-green-700' 
                                    : isTomorrow 
                                      ? 'bg-blue-100 text-blue-700'
                                      : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {isToday ? 'Today' : isTomorrow ? 'Tomorrow' : 'Upcoming'}
                                </span>
                              </div>
                              
                              <div className="bg-gray-50 rounded p-2 border border-gray-100">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium text-gray-700">
                                    {nextDate.toLocaleDateString('en-US', { 
                                      weekday: 'short',
                                      month: 'short', 
                                      day: 'numeric' 
                                    })}
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                                    assignment.assignmentType === 'project' 
                                      ? 'bg-primary text-white' 
                                      : assignment.assignmentType === 'manual'
                                      ? 'bg-accent text-white'
                                      : 'bg-gray-500 text-white'
                                  }`}>
                                    {assignment.assignmentType === 'project' ? 'Project' : 
                                     assignment.assignmentType === 'manual' ? 'Manual' : 'Day'}
                                  </span>
                                </div>
                                
                                {assignment.assignmentType === 'project' && assignment.projectName && (
                                  <div className="text-xs text-accent-dark font-medium mt-1 truncate">
                                    {assignment.projectName}
                                  </div>
                                )}
                                
                                {assignment.assignmentType === 'manual' && assignment.notes && (
                                  <div className="text-xs text-gray-600 mt-1 truncate">
                                    {assignment.notes}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                    })()}
                  </div>
                )}
              </div>
            </>
          )}
          {activeTab === 'teams' && renderTeams()}

          {activeTab === 'appointments' && renderAppointments()}

        </div>

        {/* Quick Actions Sidebar */}
        <div className="fixed right-8 bottom-8 z-40">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
            <button
              onClick={() => setShowQuickActions(!showQuickActions)}
              className="flex items-center justify-between w-full px-6 py-4 bg-gradient-to-r from-primary to-primary-dark text-white font-semibold hover:from-primary-dark hover:to-primary transition-all duration-200"
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Quick Actions</span>
              </div>
              <svg 
                className={`w-5 h-5 transition-transform duration-200 ${showQuickActions ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showQuickActions && (
              <div className="p-4 space-y-3 bg-gray-50">
                <button
                  onClick={() => { setModalType('appointment'); setShowAddModal(true); }}
                  className="w-full flex items-center space-x-3 bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-700 px-4 py-3 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <span className="font-medium">Add Appointment</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/projects')}
                  className="w-full flex items-center space-x-3 bg-white hover:bg-green-50 text-gray-700 hover:text-green-700 px-4 py-3 rounded-xl border border-gray-200 hover:border-green-300 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <span className="font-medium">Add Project</span>
                </button>
                <button
                  onClick={() => { setModalType('team'); setShowAddModal(true); }}
                  className="w-full flex items-center space-x-3 bg-white hover:bg-purple-50 text-gray-700 hover:text-purple-700 px-4 py-3 rounded-xl border border-gray-200 hover:border-purple-300 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <span className="font-medium">Add Team Member</span>
                </button>
                <button
                  onClick={() => { setModalType('reminder'); setShowAddModal(true); }}
                  className="w-full flex items-center space-x-3 bg-white hover:bg-orange-50 text-gray-700 hover:text-orange-700 px-4 py-3 rounded-xl border border-gray-200 hover:border-orange-300 transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </div>
                  <span className="font-medium">Add Reminder</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {renderModal()}

        {renderQuoteSelectionModal()}

        {renderDayModal()}

        {/* Assignment Modal */}
        {showAssignmentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  {isEditingAssignment ? 'Edit Assignment' : (assignmentType === 'project' ? 'Assign to Project' : 'Manual Assignment')}
                </h3>
                <button
                  onClick={() => {
                    setShowAssignmentModal(false);
                    setSelectedWorkDays([]);
                    setIsEditingAssignment(false);
                    setEditingAssignmentGroup(null);
                    setProjectSearch('');
                    setShowProjectDropdown(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Step 1: Project Selection (if project assignment) */}
                {assignmentType === 'project' && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Step 1: Select Project</h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Choose Project *</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={projectSearch}
                          onChange={(e) => {
                            setProjectSearch(e.target.value);
                            setShowProjectDropdown(true);
                          }}
                          onFocus={() => setShowProjectDropdown(true)}
                          onBlur={() => {
                            // Delay hiding dropdown to allow clicking
                            setTimeout(() => setShowProjectDropdown(false), 200);
                          }}
                          placeholder="Search for a project..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                        {showProjectDropdown && (
                          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {projects
                              .filter(project => 
                                project.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
                                (project.client && project.client.toLowerCase().includes(projectSearch.toLowerCase()))
                              )
                              .map(project => {
                                const startDate = project.startDate?.toDate ? project.startDate.toDate() : new Date(project.startDate);
                                const endDate = project.deadline?.toDate ? project.deadline.toDate() : new Date(project.deadline);
                                return (
                                  <div
                                    key={project.id}
                                    onClick={() => {
                                      setSelectedProjectForAssignment(project.id);
                                      setProjectSearch(project.name);
                                      setShowProjectDropdown(false);
                                      
                                      // Auto-populate dates when project is selected
                                      const startDate = project.startDate?.toDate ? project.startDate.toDate() : new Date(project.startDate);
                                      const endDate = project.deadline?.toDate ? project.deadline.toDate() : new Date(project.deadline);
                                      
                                      setAssignmentStartDate(startDate.toISOString().split('T')[0]);
                                      setAssignmentEndDate(endDate.toISOString().split('T')[0]);
                                      setSelectedWorkDays([]); // Reset work days
                                    }}
                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="font-medium text-gray-900">{project.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {project.client || 'No client'} • {startDate.toLocaleDateString()} to {endDate.toLocaleDateString()}
                                    </div>
                                  </div>
                                );
                              })}
                            {projects.filter(project => 
                              project.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
                              (project.client && project.client.toLowerCase().includes(projectSearch.toLowerCase()))
                            ).length === 0 && (
                              <div className="px-3 py-2 text-gray-500 text-sm">
                                No projects found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Date Range Selection */}
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">
                    Step {assignmentType === 'project' ? '2' : '1'}: Set Assignment Duration
                  </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date * {assignmentType === 'project' && selectedProjectForAssignment && <span className="text-gray-500">(from project)</span>}
                      </label>
                    <input
                      type="date"
                      value={assignmentStartDate}
                      onChange={(e) => {
                        setAssignmentStartDate(e.target.value);
                        setSelectedWorkDays([]); // Reset work days when date changes
                      }}
                        readOnly={assignmentType === 'project' && selectedProjectForAssignment}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                          assignmentType === 'project' && selectedProjectForAssignment ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      required
                    />
                  </div>
                  <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date * {assignmentType === 'project' && selectedProjectForAssignment && <span className="text-gray-500">(from project)</span>}
                      </label>
                    <input
                      type="date"
                      value={assignmentEndDate}
                      onChange={(e) => {
                        setAssignmentEndDate(e.target.value);
                        setSelectedWorkDays([]); // Reset work days when date changes
                      }}
                      min={assignmentStartDate}
                        readOnly={assignmentType === 'project' && selectedProjectForAssignment}
                        className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                          assignmentType === 'project' && selectedProjectForAssignment ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                      required
                    />
                  </div>
                </div>

                {/* Date Range Summary */}
                {assignmentStartDate && assignmentEndDate && (
                    <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg mt-3">
                    📅 Assignment period: {new Date(assignmentStartDate).toLocaleDateString()} to {new Date(assignmentEndDate).toLocaleDateString()}
                  </div>
                )}
                  </div>

                {/* Manual Assignment Notes (if manual assignment) */}
                {assignmentType === 'manual' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Notes *</label>
                    <textarea
                      value={manualAssignmentNotes}
                      onChange={(e) => setManualAssignmentNotes(e.target.value)}
                      placeholder="Describe what this team member will be working on..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                      rows="4"
                      required
                    />
                    {assignmentType === 'manual' && !manualAssignmentNotes.trim() && (
                      <p className="text-red-500 text-sm mt-1">Assignment notes are required for manual assignments</p>
                    )}
                  </div>
                )}

                {/* Step 3: Work Day Selection */}
                {assignmentStartDate && assignmentEndDate && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Step {assignmentType === 'project' ? '3' : '2'}: Choose Work Days
                    </h4>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-700">Select which days they'll work:</label>
                      <button
                        onClick={() => {
                          const allDays = [];
                          const startDate = new Date(assignmentStartDate);
                          const endDate = new Date(assignmentEndDate);
                          
                          for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
                            allDays.push(date.toISOString().split('T')[0]);
                          }
                          
                          setSelectedWorkDays(allDays);
                        }}
                        className="text-sm text-primary hover:text-primary-dark font-medium bg-primary/10 px-3 py-1 rounded-lg"
                      >
                        Select All Days
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-2 mb-4">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                          {day}
                        </div>
                      ))}
                      {(() => {
                        const days = [];
                        const startDate = new Date(assignmentStartDate);
                        const endDate = new Date(assignmentEndDate);
                        
                        // Add empty cells for days before the start date
                        const firstDayOfWeek = startDate.getDay();
                        for (let i = 0; i < firstDayOfWeek; i++) {
                          days.push(<div key={`empty-${i}`} className="h-8 bg-gray-50 rounded"></div>);
                        }
                        
                        // Add cells for each day in the range
                        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
                          const dateString = date.toISOString().split('T')[0];
                          const isSelected = selectedWorkDays.includes(dateString);
                          const dayOfWeek = date.getDay();
                          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                          
                          days.push(
                            <button
                              key={dateString}
                              onClick={() => {
                                if (isSelected) {
                                  setSelectedWorkDays(prev => prev.filter(d => d !== dateString));
                                } else {
                                  setSelectedWorkDays(prev => [...prev, dateString]);
                                }
                              }}
                              className={`h-8 rounded text-xs font-medium transition-colors ${
                                isSelected 
                                  ? 'bg-primary text-white' 
                                  : isWeekend 
                                    ? 'bg-gray-100 text-gray-500 hover:bg-gray-200' 
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {date.getDate()}
                            </button>
                          );
                        }
                        
                        return days;
                      })()}
                    </div>
                    
                    {selectedWorkDays.length > 0 && (
                      <div className="text-sm text-gray-600 bg-green-50 p-3 rounded-lg">
                        ✅ Selected {selectedWorkDays.length} work day{selectedWorkDays.length > 1 ? 's' : ''}
                      </div>
                    )}
                  </div>
                )}

                {/* Email Notification Option */}
                {selectedTeamMemberForAssignment && teams.find(t => t.id === selectedTeamMemberForAssignment)?.email && (
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="emailNotification"
                        checked={sendEmailNotification}
                        onChange={(e) => setSendEmailNotification(e.target.checked)}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <label htmlFor="emailNotification" className="text-sm text-gray-700">
                        <span className="font-medium">Send email notification</span>
                        <span className="text-gray-500 ml-1">
                          to {teams.find(t => t.id === selectedTeamMemberForAssignment)?.name} 
                          ({teams.find(t => t.id === selectedTeamMemberForAssignment)?.email})
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 mt-1 ml-7">
                      {isEditingAssignment ? 'Update notification will be sent' : 'New assignment notification will be sent'}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      setShowAssignmentModal(false);
                      setSelectedWorkDays([]);
                      setIsEditingAssignment(false);
                      setEditingAssignmentGroup(null);
                      setSendEmailNotification(true); // Reset to default
                      setProjectSearch('');
                      setShowProjectDropdown(false);
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAssignmentFromTeams}
                    disabled={
                      !assignmentStartDate ||
                      !assignmentEndDate ||
                      selectedWorkDays.length === 0 ||
                      (assignmentType === 'project' && !selectedProjectForAssignment) ||
                      (assignmentType === 'manual' && !manualAssignmentNotes.trim())
                    }
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isEditingAssignment ? 'Update Assignment' : 'Save Assignment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Member Schedule Modal */}
        {showTeamMemberSchedule && selectedTeamMemberForSchedule && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-6xl h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedTeamMemberForSchedule.name}'s Schedule
                  </h3>
                  <p className="text-gray-600">
                    {selectedTeamMemberForSchedule.role}
                  </p>
                </div>
                <button
                  onClick={() => setShowTeamMemberSchedule(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Team Member Schedule Calendar */}
              <div className="bg-gray-50 rounded-xl p-6">
                {/* Calendar Header with Navigation */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => {
                      const prevMonth = new Date(teamMemberScheduleDate);
                      prevMonth.setMonth(prevMonth.getMonth() - 1);
                      setTeamMemberScheduleDate(prevMonth);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h4 className="text-lg font-semibold text-gray-900">
                    {teamMemberScheduleDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </h4>
                  <button
                    onClick={() => {
                      const nextMonth = new Date(teamMemberScheduleDate);
                      nextMonth.setMonth(nextMonth.getMonth() + 1);
                      setTeamMemberScheduleDate(nextMonth);
                    }}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => {
                    const isWeekend = day === 'Sun' || day === 'Sat';
                    return (
                      <div key={day} className={`text-center text-sm font-medium py-2 ${isWeekend && !showWeekends ? 'text-gray-300' : 'text-gray-700'}`}>
                        {day}
                      </div>
                    );
                  })}
                </div>
                
                <div className="grid grid-cols-7 gap-1">
                  {(() => {
                    const daysInMonth = new Date(teamMemberScheduleDate.getFullYear(), teamMemberScheduleDate.getMonth() + 1, 0).getDate();
                    const firstDayOfMonth = new Date(teamMemberScheduleDate.getFullYear(), teamMemberScheduleDate.getMonth(), 1).getDay();
                    const days = [];

                    // Add empty cells for days before the first day of the month
                    for (let i = 0; i < firstDayOfMonth; i++) {
                      days.push(<div key={`empty-${i}`} className="h-16 bg-gray-100 rounded"></div>);
                    }

                    // Add cells for each day of the month
                    for (let day = 1; day <= daysInMonth; day++) {
                      const date = new Date(teamMemberScheduleDate.getFullYear(), teamMemberScheduleDate.getMonth(), day);
                      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
                      const isWeekend = (dayOfWeek === 0 || dayOfWeek === 6) && !showWeekends;
                      const dateKey = date.toISOString().split('T')[0];
                      const dayAssignments = allDayAssignments[dateKey] || [];
                      
                      // Get all projects whose date range includes this day
                      const projectsForDay = projects.filter(project => {
                        const startDate = project.startDate?.toDate ? project.startDate.toDate() : new Date(project.startDate);
                        const deadline = project.deadline?.toDate ? project.deadline.toDate() : (project.deadline ? new Date(project.deadline) : null);
                        
                        if (deadline) {
                          const isInTimeline = date >= startDate && date <= deadline;
                          
                          // Check weekend restrictions
                          if (isWeekend) {
                            const weekendDay = dayOfWeek === 0 ? 'Sunday' : 'Saturday';
                            const projectWeekendDays = project.weekendDays || 'None';
                            
                            // If project doesn't allow any weekends, exclude it
                            if (projectWeekendDays === 'None') {
                              return false;
                            }
                            
                            // If it's Saturday but project only allows Sunday
                            if (weekendDay === 'Saturday' && projectWeekendDays === 'Sunday Only') {
                              return false;
                            }
                            
                            // If it's Sunday but project only allows Saturday
                            if (weekendDay === 'Sunday' && projectWeekendDays === 'Saturday Only') {
                              return false;
                            }
                          }
                          
                          return isInTimeline;
                        } else {
                          const isStartDate = date.toDateString() === startDate.toDateString();
                          
                          // Check weekend restrictions for start date
                          if (isWeekend) {
                            const weekendDay = dayOfWeek === 0 ? 'Sunday' : 'Saturday';
                            const projectWeekendDays = project.weekendDays || 'None';
                            
                            // If project doesn't allow any weekends, exclude it
                            if (projectWeekendDays === 'None') {
                              return false;
                            }
                            
                            // If it's Saturday but project only allows Sunday
                            if (weekendDay === 'Saturday' && projectWeekendDays === 'Sunday Only') {
                              return false;
                            }
                            
                            // If it's Sunday but project only allows Saturday
                            if (weekendDay === 'Sunday' && projectWeekendDays === 'Saturday Only') {
                              return false;
                            }
                          }
                          
                          return isStartDate;
                        }
                      });

                      // For each project, check if this team member is assigned on this day
                      const projectBadges = projectsForDay.map((project, index) => {
                        const isAssigned = dayAssignments.some(a =>
                          a.assignmentType === 'project' &&
                          a.projectId === project.id &&
                          a.memberId === selectedTeamMemberForSchedule.id
                        );
                        return (
                          <div
                            key={project.id}
                            className={`text-sm px-2 py-1 rounded truncate ${
                              isAssigned ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'
                            }`}
                            title={`${project.name}${isAssigned ? ' (Assigned)' : ' (Click to assign)'}`}
                          >
                            {(project.name || 'Project').substring(0, 12)}
                          </div>
                        );
                      });

                      // Show manual assignments for this team member
                      const memberAssignments = dayAssignments.filter(assignment => assignment.memberId === selectedTeamMemberForSchedule.id);
                      const manualBadges = memberAssignments.filter(assignment => assignment.assignmentType === 'manual').slice(0, 4).map((assignment, index) => (
                        <div
                          key={assignment.id}
                          className="text-sm px-2 py-1 rounded truncate bg-orange-100 text-orange-800"
                          title={assignment.notes}
                        >
                          {(assignment.notes || 'Manual').substring(0, 12)}
                        </div>
                      ));

                      days.push(
                        <div
                          key={day}
                          onClick={() => handleTeamMemberScheduleDayClick(date)}
                          className={`h-24 border border-gray-200 rounded p-1 cursor-pointer hover:bg-gray-50 transition-colors ${
                            isWeekend ? 'bg-gray-100 opacity-60' : 'bg-white'
                          } ${
                            projectsForDay.length > 0 ? 'ring-2 ring-blue-200' : ''
                          }`}
                          title={`${date.toLocaleDateString()} - Click to view details`}
                        >
                          <div className={`text-xs font-medium mb-1 ${isWeekend ? 'text-gray-500' : 'text-gray-900'}`}>{day}</div>
                          {!isWeekend ? (
                            <div className="space-y-1 max-h-16 overflow-hidden">
                              {projectBadges}
                              {manualBadges}
                              {memberAssignments.filter(assignment => assignment.assignmentType === 'manual').length > 4 && (
                                <div className="text-sm text-gray-500 text-center bg-gray-50 px-2 py-1 rounded">
                                  +{memberAssignments.filter(assignment => assignment.assignmentType === 'manual').length - 4} more
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="mt-1">
                              <div className="text-xs text-gray-400 italic">Weekend</div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return days;
                  })()}
                </div>
              </div>

              {/* Assignment Summary */}
              <div className="mt-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Assignment Summary</h4>
                <div className="space-y-3">
                  {(() => {
                    const allAssignments = Object.values(allDayAssignments).flat().filter(assignment => 
                      assignment.memberId === selectedTeamMemberForSchedule.id
                    ).sort((a, b) => new Date(a.date) - new Date(b.date));

                    if (allAssignments.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500">
                          No assignments scheduled for {selectedTeamMemberForSchedule.name}
                        </div>
                      );
                    }

                    // Group assignments by project/assignment type and date range
                    const groupedAssignments = {};
                    allAssignments.forEach(assignment => {
                      const key = `${assignment.assignmentType}-${assignment.projectId || 'manual'}-${assignment.startDate?.toDate ? assignment.startDate.toDate().toISOString() : new Date(assignment.startDate).toISOString()}-${assignment.endDate?.toDate ? assignment.endDate.toDate().toISOString() : new Date(assignment.endDate).toISOString()}`;
                      
                      if (!groupedAssignments[key]) {
                        groupedAssignments[key] = {
                          assignmentType: assignment.assignmentType,
                          projectName: assignment.projectName,
                          notes: assignment.notes,
                          startDate: assignment.startDate,
                          endDate: assignment.endDate,
                          workDays: assignment.workDays || [],
                          assignments: []
                        };
                      }
                      groupedAssignments[key].assignments.push(assignment);
                    });

                    return Object.values(groupedAssignments).map((group, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              group.assignmentType === 'project' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {group.assignmentType === 'project' ? 'Project' : 'Manual'}
                            </span>
                            {group.startDate && group.endDate ? (
                              <span className="text-sm text-gray-600">
                                {`${group.startDate?.toDate ? 
                                  group.startDate.toDate().toLocaleDateString() : 
                                  new Date(group.startDate).toLocaleDateString()
                                } - ${group.endDate?.toDate ? 
                                  group.endDate.toDate().toLocaleDateString() : 
                                  new Date(group.endDate).toLocaleDateString()
                                }`}
                              </span>
                            ) : group.assignments[0]?.date && (
                              <span className="text-sm text-gray-600">
                                {group.assignments[0].date?.toDate ? 
                                  group.assignments[0].date.toDate().toLocaleDateString() : 
                                  new Date(group.assignments[0].date).toLocaleDateString()
                                }
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => handleEditAssignment(group)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="Edit assignment"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleSendAssignmentNotification(group)}
                              className="text-green-600 hover:text-green-800 p-1"
                              title="Send update notification"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            </button>
                          <button
                            onClick={() => handleRemoveAssignmentGroup(group.assignments)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove all assignments in this group"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                          </div>
                        </div>
                        
                        <div className="text-sm text-gray-900 mb-3">
                          {group.assignmentType === 'project' ? (
                            <span className="font-medium">{group.projectName}</span>
                          ) : (
                            <span>{group.notes}</span>
                          )}
                        </div>

                        {/* Show selected work days if not all days are selected */}
                        {group.workDays && group.workDays.length > 0 && (
                          <div className="mt-3">
                            <div className="text-xs text-gray-600 mb-2">Selected work days:</div>
                            <div className="flex flex-wrap gap-1">
                              {group.workDays.map((workDay, dayIndex) => {
                                const workDate = new Date(workDay);
                                return (
                                  <span
                                    key={dayIndex}
                                    className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                                  >
                                    {workDate.toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      weekday: 'short'
                                    })}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}

                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Team Member Day Detail Modal */}
        {showTeamMemberDayDetail && selectedTeamMemberDay && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {selectedTeamMemberForSchedule.name}'s Schedule - {selectedTeamMemberDay.date.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </h3>
                  <p className="text-gray-600">
                    {selectedTeamMemberDay.assignments.length} assignment{selectedTeamMemberDay.assignments.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <button
                  onClick={() => setShowTeamMemberDayDetail(false)}
                  className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {selectedTeamMemberDay.assignments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-lg font-medium">No assignments scheduled</p>
                  <p className="text-sm">This team member has no assignments for this day.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {selectedTeamMemberDay.assignments.map((assignment, index) => (
                    <div key={assignment.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                            assignment.assignmentType === 'project' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {assignment.assignmentType === 'project' ? 'Project' : 'Manual'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {assignment.date?.toDate ? 
                              assignment.date.toDate().toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              }) : 
                              new Date(assignment.date).toLocaleTimeString('en-US', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })
                            }
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              // Create a simple group for editing this single assignment
                              const assignmentGroup = {
                                assignmentType: assignment.assignmentType,
                                projectName: assignment.projectName,
                                notes: assignment.notes,
                                startDate: assignment.startDate,
                                endDate: assignment.endDate,
                                workDays: assignment.workDays || [],
                                assignments: [assignment]
                              };
                              setShowTeamMemberDayDetail(false);
                              handleEditAssignment(assignmentGroup);
                            }}
                            className="text-blue-600 hover:text-blue-800 p-1"
                            title="Edit assignment"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={async () => {
                              await handleRemoveSingleDayAssignment(assignment.id, selectedTeamMemberDay.date);
                              // Refresh the day detail modal data
                              refreshDayDetailModal();
                            }}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Remove assignment"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      <div className="text-lg font-medium text-gray-900 mb-2">
                        {assignment.assignmentType === 'project' ? (
                          <span>{assignment.projectName}</span>
                        ) : (
                          <span>{assignment.notes}</span>
                        )}
                      </div>

                      {assignment.assignmentType === 'project' && assignment.projectName && (
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Project:</span> {assignment.projectName}
                        </div>
                      )}

                      {assignment.assignmentType === 'manual' && assignment.notes && (
                        <div className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">Notes:</span> {assignment.notes}
                        </div>
                      )}

                      {assignment.startDate && assignment.endDate ? (
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">Duration:</span> {`${assignment.startDate?.toDate ? 
                            assignment.startDate.toDate().toLocaleDateString() : 
                            new Date(assignment.startDate).toLocaleDateString()
                          } - ${assignment.endDate?.toDate ? 
                            assignment.endDate.toDate().toLocaleDateString() : 
                            new Date(assignment.endDate).toLocaleDateString()
                          }`}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">Duration:</span> Single day assignment
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <>
            <div style={{position: 'fixed', top: '-100vh', left: '-100vw', right: '-100vw', bottom: '-100vh', background: 'rgba(0,0,0,0.7)', zIndex: 99999}} />
            <div style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-900">Delete {itemToDelete?.type === 'team' ? 'Team Member' : 'Appointment'}</h3>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-gray-700">
                    Are you sure you want to delete this <strong>{itemToDelete?.type === 'team' ? 'team member' : 'appointment'}</strong>?
                  </p>
                  <p className="text-sm text-gray-500 mt-2">
                    This action cannot be undone. All associated data and assignments will be permanently removed.
                  </p>
                </div>
                
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Delete {itemToDelete?.type === 'team' ? 'Team Member' : 'Appointment'}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default Scheduling; 